import React, { useState, memo, useEffect, useRef, useId } from "react";
import { X, Eye, EyeOff, Gift } from "lucide-react";
import { authAPI } from "../services/api";

/**
 * AuthModal component - extracted from App.jsx to prevent form state resets.
 *
 * ISSUE: When AuthModal was defined inside App(), every App re-render created a
 * new component identity, unmounting the previous AuthModal and resetting form state.
 *
 * FIX: Extract as standalone memoized component. Now form state persists across
 * unrelated parent re-renders (cart updates, notifications, etc.).
 *
 * Per constitution_ui.json §6: "Every action returns feedback state; critical flows persist"
 *
 * USER-CON-001: Enhanced registration with first_name, last_name, birth_date for gamification
 * BR-AUTH-SIGNIN-NO-PASSWORD-RESET-001 PHASE_C: reset_request view + Forgot password link +
 *   ARIA dialog + Escape handler + focus trap (BR-GAP-014 a11y baseline).
 */
const AuthModal = memo(function AuthModal({ mode, setMode, onClose, onLogin, onSignup }) {
  // USER-CON-001: Split name into first/last for enhanced user profiles
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  // USER-CON-001: Birthday for birthday bonus points
  const [birthDate, setBirthDate] = useState("");
  // USER-CON-001: Legal checkboxes
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  // BR-001/BR-007: reset_request view state — flash a canonical "if an account exists..." message
  const [resetRequestStatus, setResetRequestStatus] = useState("idle"); // "idle" | "sent"

  const titleId = useId();
  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);

  const TTL_MINUTES = Number(import.meta.env?.VITE_PASSWORD_RESET_TOKEN_MINUTES || 30);

  const validateEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  // BR-GAP-014: Escape dismisses the modal (when not loading)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !isLoading) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isLoading, onClose]);

  // BR-GAP-014: initial focus management — focus the first input when the view enters
  useEffect(() => {
    firstInputRef.current?.focus?.();
    setErrors({});
  }, [mode]);

  // BR-GAP-014: focus trap via Tab/Shift+Tab cycling within the dialog
  const onDialogKeyDown = (e) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusables = dialogRef.current.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    /* v8 ignore next 1 -- defensive guard; the rendered dialog always contains focusable elements (X button + form inputs) */
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const handleResetRequestSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!validateEmail(email)) newErrors.email = "Valid email is required";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsLoading(true);
    try {
      await authAPI.requestPasswordReset(email.trim());
      // Anti-enum: always show the canonical message regardless of backend response.
      setResetRequestStatus("sent");
    } catch {
      // Swallow non-429 errors and still show canonical message — anti-enum parity
      setResetRequestStatus("sent");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (mode === "signup") {
      if (!firstName.trim()) newErrors.firstName = "First name is required";
      if (!lastName.trim()) newErrors.lastName = "Last name is required";
      if (!validateEmail(email)) newErrors.email = "Valid email is required";
      if (password.length < 6) newErrors.password = "Password must be at least 6 characters";
      if (password !== confirmPassword) newErrors.confirmPassword = "Passwords don't match";
      if (!acceptTerms) newErrors.acceptTerms = "You must accept the Terms of Service";
      if (!acceptPrivacy) newErrors.acceptPrivacy = "You must accept the Privacy Policy";

      if (Object.keys(newErrors).length === 0) {
        setIsLoading(true);
        try {
          const fullName = `${firstName.trim()} ${lastName.trim()}`;
          await onSignup(fullName, email.trim(), password, {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            birth_date: birthDate || null,
            accept_terms: acceptTerms,
            accept_privacy: acceptPrivacy,
          });
        } finally {
          setIsLoading(false);
        }
      }
    } else {
      if (!email.trim()) newErrors.email = "Email is required";
      if (!password) newErrors.password = "Password is required";

      if (Object.keys(newErrors).length === 0) {
        setIsLoading(true);
        try {
          await onLogin(email.trim(), password);
        } finally {
          setIsLoading(false);
        }
      }
    }

    setErrors(newErrors);
  };

  const switchMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setErrors({});
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setBirthDate("");
    setAcceptTerms(false);
    setAcceptPrivacy(false);
  };

  const headerTitle =
    mode === "login" ? "WELCOME BACK" : mode === "signup" ? "JOIN THE CREW" : "RESET PASSWORD";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onDialogKeyDown}
        className="relative bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-md mx-4 slide-in shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-800">
          <h3 id={titleId} className="font-comic text-2xl text-white">
            {headerTitle}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* RESET REQUEST VIEW (BR-001/BR-007/BR-015 + BR-GAP-010 TTL surfacing) */}
        {mode === "reset_request" && (
          <form className="p-6 space-y-4" onSubmit={handleResetRequestSubmit} noValidate>
            {resetRequestStatus === "sent" ? (
              <div className="space-y-3">
                <p className="text-zinc-200">
                  If an account with that email exists, a reset link has been sent.
                </p>
                <p className="text-zinc-500 text-sm">
                  Check your inbox — the link expires in {TTL_MINUTES} minutes. Don&rsquo;t see it?
                  Check your spam folder.
                </p>
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-orange-500 hover:underline text-sm"
                >
                  Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <p className="text-zinc-400 text-sm">
                  Enter the email on your account and we&rsquo;ll send a reset link.
                </p>
                <div>
                  <label htmlFor="reset-email" className="block text-sm text-zinc-400 mb-1">
                    Email
                  </label>
                  <input
                    ref={firstInputRef}
                    id="reset-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={isLoading}
                    className={`w-full px-4 py-3 bg-zinc-800 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 ${
                      errors.email ? "border-red-500" : "border-zinc-700"
                    }`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-3 bg-orange-500 rounded-xl font-bold text-white hover:shadow-lg hover:shadow-orange-500/25 disabled:opacity-50"
                >
                  {isLoading ? "Sending…" : "Send reset link"}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-zinc-500 hover:text-zinc-300 text-sm w-full text-center"
                >
                  Back to Sign In
                </button>
              </>
            )}
          </form>
        )}

        {/* LOGIN / SIGNUP VIEWS */}
        {mode !== "reset_request" && (
          <form className="p-6" onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              {mode === "signup" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="first-name" className="block text-sm text-zinc-400 mb-1">
                        First Name
                      </label>
                      <input
                        /* v8 ignore next 1 -- this input only renders when mode === "signup"; ternary's null branch is structurally unreachable */
                        ref={mode === "signup" ? firstInputRef : null}
                        id="first-name"
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="First"
                        disabled={isLoading}
                        className={`w-full px-4 py-3 bg-zinc-800 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 ${
                          errors.firstName ? "border-red-500" : "border-zinc-700"
                        }`}
                      />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label htmlFor="last-name" className="block text-sm text-zinc-400 mb-1">
                        Last Name
                      </label>
                      <input
                        id="last-name"
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Last"
                        disabled={isLoading}
                        className={`w-full px-4 py-3 bg-zinc-800 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 ${
                          errors.lastName ? "border-red-500" : "border-zinc-700"
                        }`}
                      />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                    </div>
                  </div>
                </>
              )}

              <div>
                <label htmlFor="auth-email" className="block text-sm text-zinc-400 mb-1">
                  Email
                </label>
                <input
                  ref={mode === "login" ? firstInputRef : null}
                  id="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  className={`w-full px-4 py-3 bg-zinc-800 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 ${
                    errors.email ? "border-red-500" : "border-zinc-700"
                  }`}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="auth-password" className="block text-sm text-zinc-400 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="auth-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={isLoading}
                    className={`w-full px-4 py-3 pr-12 bg-zinc-800 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 ${
                      errors.password ? "border-red-500" : "border-zinc-700"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                {/* BR-001/BR-002/BR-015: Forgot password link below password field in Sign In view */}
                {mode === "login" && (
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => setMode("reset_request")}
                      className="text-orange-500 hover:text-orange-400 text-sm font-semibold"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>

              {mode === "signup" && (
                <div>
                  <label htmlFor="confirm-password" className="block text-sm text-zinc-400 mb-1">
                    Confirm Password
                  </label>
                  <input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={isLoading}
                    className={`w-full px-4 py-3 bg-zinc-800 border rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50 ${
                      errors.confirmPassword ? "border-red-500" : "border-zinc-700"
                    }`}
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                  )}
                </div>
              )}

              {mode === "signup" && (
                <div>
                  <label htmlFor="birth-date" className="block text-sm text-zinc-400 mb-1 flex items-center gap-2">
                    Birthday <span className="text-zinc-600">(optional)</span>
                    <Gift className="w-4 h-4 text-orange-500" />
                  </label>
                  <input
                    id="birth-date"
                    type="date"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-50"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Earn bonus points on your birthday!</p>
                </div>
              )}

              {mode === "signup" && (
                <div className="space-y-3 pt-2">
                  <label className={`flex items-start gap-3 cursor-pointer group ${errors.acceptTerms ? 'text-red-400' : ''}`}>
                    <input
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      disabled={isLoading}
                      className="mt-1 w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-zinc-900"
                    />
                    <span className="text-sm text-zinc-400 group-hover:text-zinc-300">
                      I agree to the{" "}
                      <a
                        href="/legal/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:underline"
                      >
                        Terms of Service
                      </a>
                    </span>
                  </label>
                  {errors.acceptTerms && <p className="text-red-500 text-xs ml-7">{errors.acceptTerms}</p>}

                  <label className={`flex items-start gap-3 cursor-pointer group ${errors.acceptPrivacy ? 'text-red-400' : ''}`}>
                    <input
                      type="checkbox"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                      disabled={isLoading}
                      className="mt-1 w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-zinc-900"
                    />
                    <span className="text-sm text-zinc-400 group-hover:text-zinc-300">
                      I agree to the{" "}
                      <a
                        href="/legal/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-orange-500 hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                  {errors.acceptPrivacy && <p className="text-red-500 text-xs ml-7">{errors.acceptPrivacy}</p>}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-orange-500 rounded-xl font-bold text-white hover:shadow-lg hover:shadow-orange-500/25 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {mode === "login" ? "Signing In..." : "Creating Account..."}
                  </span>
                ) : (
                  mode === "login" ? "Sign In" : "Create Account"
                )}
              </button>
            </div>

            {/* Demo credentials hint - DEV ONLY per constitution_cyberSec.json Section 3 */}
            {mode === "login" && import.meta.env.DEV && (
              <div className="mt-4 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-xs text-zinc-500 text-center">
                  <span className="text-orange-500">Demo:</span> demo@maestrograding.ai / demo123
                </p>
              </div>
            )}

            {/* Switch mode */}
            <div className="mt-6 text-center">
              <p className="text-zinc-500 text-sm">
                {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
                <button
                  type="button"
                  onClick={switchMode}
                  disabled={isLoading}
                  className="text-orange-500 hover:text-orange-400 font-semibold transition-colors disabled:opacity-50"
                >
                  {mode === "login" ? "Sign Up" : "Sign In"}
                </button>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
});

export default AuthModal;
